import './fabricDefaults.ts';
import {
    Circle,
    FabricImage,
    FabricObject,
    Group,
    IText,
    Rect,
    Shadow,
    Textbox,
    classRegistry
} from 'fabric';
import {User} from 'lucide';
import {customIconNodes} from '../shared/customIcons.ts';
import {makeGlyph} from './resourceCardIcons.ts';
import {projectTabUrl} from '../../lib/projectUrl.ts';

const DEFAULT_WIDTH = 220;
// The same minimum the agent tools enforce (WhiteboardBuilder.CardMinimumWidth).
const MIN_WIDTH = 160;
const PADDING = 10;
const THUMB_RATIO = 0.56; // roughly 16:9
const AVATAR_SIZE = 24;
const CORNER = 8;
const TITLE_FONT_SIZE = 13;
const BYLINE_FONT_SIZE = 10;
const TITLE_GAP = 8;
const ROW_GAP = 8;
const TITLE_COLOR = '#1F1F1F';
const BYLINE_COLOR = '#6B6B6B';
const BORDER_COLOR = '#D9D9D9';
const PLACEHOLDER_COLOR = '#E9ECF2';
const PLACEHOLDER_GLYPH_COLOR = '#9AA3B2';

export const ProjectCardDefaultFontFamily = 'Poppins, sans-serif';

export interface ProjectCardOptions {
    left?: number;
    top?: number;
    width?: number;
    scaleX?: number;
    scaleY?: number;
    angle?: number;
    flipX?: boolean;
    flipY?: boolean;
    /** What /project/<ref> uses: the custom url when there is one, else the id. */
    projectRef?: string;
    /** Kept so the card can be refreshed against the live project later. */
    mixId?: string;
    name?: string;
    nickname?: string;
    thumbnail?: string;
    userThumbnail?: string;
    fill?: string;
    /**
     * The tab the card opens, as a ?tab= slot name ('project', 'tab2'...).
     * Undefined on cards that have never had one, which open the project's
     * first tab; null once one has been cleared, so the clearing syncs.
     */
    tab?: string | null;
    /** The tab's title when it was picked, shown in the byline. */
    tabTitle?: string | null;
    /** Opens the tab full screen, for the tab types that have one. */
    fullscreen?: boolean;
}

export interface ProjectCardTarget {
    tab: string | null;
    tabTitle: string | null;
    fullscreen: boolean;
}

/*
 * A link to another project, as a card on the whiteboard.
 *
 * Replaces the hand-built group that CanvasController.addProjectLink used to
 * assemble. That version serialized as a generic group with eight children,
 * which measured around 11.5KB per card and was re-diffed and re-synced on
 * every drag; this one is a compact option bag of a few hundred bytes, in the
 * same shape as StickyNote and ResourceCard.
 *
 * Cards created by the old code keep working untouched: they are stored as
 * type 'group' and still restore through the generic group branch, so nothing
 * here has to understand them. See CanvasController.replaceLegacyProjectCard
 * for the opt-in upgrade.
 *
 * Image urls are only persisted when they are ordinary http(s) urls. A project
 * with no thumbnail of its own carries Constants.DefaultThumbnailDataURL, which
 * is a 65KB base64 string; storing that on the card would put every drag over
 * the 16KB SignalR threshold in useProjectData. Those cards draw a placeholder
 * instead.
 *
 * NOTE ON nav: like the old card, this sets nav to an in-app /project/ route,
 * which is what handleMouseDown hands to react-router. That is correct here and
 * is the opposite of ResourceCard, whose url is external and must never reach
 * navigate.
 */
export class ProjectCard extends Group {
    static type = 'project-card';

    body: Rect;
    thumbPlaceholder: Rect;
    avatarPlaceholder: Circle;
    titleText: Textbox;
    bylineText: IText;

    nav: string | null = null;

    private projectRef: string;
    private cardMixId: string;
    private cardName: string;
    private cardNickname: string;
    private cardThumbnail: string;
    private cardUserThumbnail: string;
    private cardTab: string | null | undefined;
    private cardTabTitle: string | null;
    private cardFullscreen: boolean;
    private baseWidth: number;

    private thumbImage: FabricImage | null = null;
    private avatarImage: FabricImage | null = null;
    private glyphs: FabricObject[] = [];

    constructor(options: ProjectCardOptions = {}) {
        const {
            projectRef = '',
            mixId = '',
            name = '',
            nickname = '',
            thumbnail = '',
            userThumbnail = '',
            fill = '#FFFFFF',
            width = DEFAULT_WIDTH,
            tab,
            tabTitle = null,
            fullscreen = false,
            ...groupOptions
        } = options;

        const body = new Rect({
            left: -width / 2,
            top: -width / 2,
            width,
            height: width,
            rx: 10,
            ry: 10,
            fill,
            stroke: BORDER_COLOR,
            strokeWidth: 1,
            selectable: false,
            evented: false,
            shadow: new Shadow({
                color: 'rgba(0, 0, 0, 0.18)',
                blur: 8,
                offsetX: 0,
                offsetY: 3
            })
        });

        const thumbPlaceholder = new Rect({
            rx: CORNER,
            ry: CORNER,
            fill: PLACEHOLDER_COLOR,
            selectable: false,
            evented: false
        });

        const avatarPlaceholder = new Circle({
            radius: AVATAR_SIZE / 2,
            fill: PLACEHOLDER_COLOR,
            selectable: false,
            evented: false
        });

        const titleText = new Textbox(name || 'Project', {
            fontSize: TITLE_FONT_SIZE,
            fontFamily: ProjectCardDefaultFontFamily,
            fontWeight: 'bold',
            fill: TITLE_COLOR,
            selectable: false,
            evented: false
        });

        const bylineText = new IText('', {
            fontSize: BYLINE_FONT_SIZE,
            fontFamily: ProjectCardDefaultFontFamily,
            fill: BYLINE_COLOR,
            selectable: false,
            evented: false
        });

        super(
            [body, thumbPlaceholder, avatarPlaceholder, titleText, bylineText],
            {
                subTargetCheck: false,
                interactive: false,
                lockScalingFlip: true
            }
        );

        this.body = body;
        this.thumbPlaceholder = thumbPlaceholder;
        this.avatarPlaceholder = avatarPlaceholder;
        this.titleText = titleText;
        this.bylineText = bylineText;

        this.projectRef = projectRef;
        this.cardMixId = mixId;
        this.cardName = name;
        this.cardNickname = nickname;
        this.cardThumbnail = ProjectCard.storableUrl(thumbnail);
        this.cardUserThumbnail = ProjectCard.storableUrl(userThumbnail);
        this.cardTab = tab;
        this.cardTabTitle = tabTitle;
        this.cardFullscreen = fullscreen;
        this.baseWidth = Math.max(MIN_WIDTH, width);

        this.refreshNav();
        this.hoverCursor = 'pointer';

        this.on('modified', this.bakeScale);

        this.layoutContent();

        this.set(groupOptions);
        this.setCoords();
    }

    /*
     * A resize gesture changes scaleX/scaleY; convert that into a real width
     * once the gesture ends, as link cards and sticky notes do, so the text
     * keeps its size and rewraps instead of being zoomed, and the stored
     * width is the width drawn. Height follows the width, so the vertical
     * scale is simply discarded.
     */
    private bakeScale = () => {
        if (this.scaleX === 1 && this.scaleY === 1) {
            return;
        }

        const width = Math.max(MIN_WIDTH, this.body.width * this.scaleX);

        this.set({scaleX: 1, scaleY: 1});
        this.baseWidth = width;
        this.layoutContent();

        if (this.canvas) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.canvas.fire('object:modified' as any, {target: this});
            this.canvas.requestRenderAll();
        }
    };

    /*
     * Only ordinary web urls are worth keeping. A data: uri would be persisted
     * verbatim into the board data, and the default project thumbnail alone is
     * 65KB of base64.
     */
    private static storableUrl(url: string): string {
        const trimmed = (url ?? '').trim();
        return /^https?:\/\//i.test(trimmed) ? trimmed : '';
    }

    private static textColumnWidth(width: number): number {
        return Math.max(width - PADDING * 2, 40);
    }

    /*
     * The byline is the owner's handle only. Canvas data is readable by
     * everyone who can see the project, so the display name (usually a real
     * name) is never written into it. Older cards that stored a userName
     * simply ignore it.
     */
    private refreshByline() {
        const handle = this.cardNickname
            ? '@' + this.cardNickname.replace(/^@/, '')
            : '';
        this.bylineText.set('text', handle || ' ');
    }

    /*
     * Names the tab the card opens after the handle, cut short with an
     * ellipsis when it would run past the card. The handle is left whole.
     */
    private fitByline(maxWidth: number) {
        this.refreshByline();
        if (!this.cardTab) {
            return;
        }

        const handle = this.bylineText.text.trim();
        const label =
            (this.cardTabTitle || 'Tab') +
            (this.cardFullscreen ? ', full screen' : '');
        const compose = (text: string) =>
            handle ? `${handle} · ${text}` : text;

        this.bylineText.set('text', compose(label));
        for (
            let length = label.length - 1;
            length > 0 && this.bylineText.width > maxWidth;
            length--
        ) {
            this.bylineText.set(
                'text',
                compose(label.slice(0, length).trimEnd() + '…')
            );
        }
    }

    private refreshNav() {
        this.nav = this.projectRef
            ? projectTabUrl({id: this.projectRef}, this.cardTab, {
                  fullscreen: this.cardFullscreen
              })
            : null;
    }

    /*
     * Fits an image to a box, filling it without distortion.
     *
     * The crop is done on the source (cropX/cropY plus width/height) rather
     * than by clipping a larger image. A clipPath hides the overflow but the
     * object keeps its full bounding box, and inside a group that inflates the
     * group's bounds and shifts the card away from where it was placed.
     *
     * The old card avoided overflow by scaling x and y independently, which is
     * why non-square thumbnails and avatars came out squashed.
     */
    private static coverImage(
        image: FabricImage,
        boxWidth: number,
        boxHeight: number,
        boxLeft: number,
        boxTop: number,
        rounded: boolean
    ) {
        const original = image.getOriginalSize();
        const naturalWidth = original.width || 1;
        const naturalHeight = original.height || 1;

        const scale = Math.max(
            boxWidth / naturalWidth,
            boxHeight / naturalHeight
        );

        const cropWidth = Math.min(naturalWidth, boxWidth / scale);
        const cropHeight = Math.min(naturalHeight, boxHeight / scale);

        image.set({
            cropX: (naturalWidth - cropWidth) / 2,
            cropY: (naturalHeight - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
            scaleX: scale,
            scaleY: scale,
            left: boxLeft,
            top: boxTop,
            selectable: false,
            evented: false
        });

        image.clipPath = rounded
            ? new Circle({
                  left: -cropWidth / 2,
                  top: -cropHeight / 2,
                  radius: Math.min(cropWidth, cropHeight) / 2
              })
            : new Rect({
                  left: -cropWidth / 2,
                  top: -cropHeight / 2,
                  width: cropWidth,
                  height: cropHeight,
                  rx: CORNER / scale,
                  ry: CORNER / scale
              });
    }

    /*
     * Positions everything from the current width. Runs before the images have
     * loaded and again afterwards, so the card is laid out and readable either
     * way; a project whose thumbnail has gone missing still gets a card rather
     * than the silent nothing the old code produced.
     */
    private layoutContent() {
        const {left, top} = this;
        const width = this.baseWidth;
        const columnWidth = ProjectCard.textColumnWidth(width);
        const thumbWidth = width - PADDING * 2;
        const thumbHeight = Math.round(thumbWidth * THUMB_RATIO);

        this.titleText.set({splitByGrapheme: false, width: columnWidth});
        if (this.titleText.width > columnWidth + 0.5) {
            // A single unbroken word wider than the card, so let it break.
            this.titleText.set({splitByGrapheme: true, width: columnWidth});
        }

        const contentHeight =
            thumbHeight +
            TITLE_GAP +
            this.titleText.height +
            ROW_GAP +
            AVATAR_SIZE;
        const height = contentHeight + PADDING * 2;

        this.body.set({
            left: -width / 2,
            top: -height / 2,
            width,
            height
        });

        const thumbLeft = -width / 2 + PADDING;
        const thumbTop = -height / 2 + PADDING;

        this.thumbPlaceholder.set({
            left: thumbLeft,
            top: thumbTop,
            width: thumbWidth,
            height: thumbHeight
        });

        const titleTop = thumbTop + thumbHeight + TITLE_GAP;
        this.titleText.set({left: thumbLeft, top: titleTop});

        const rowTop = titleTop + this.titleText.height + ROW_GAP;
        this.fitByline(columnWidth - AVATAR_SIZE - 8);
        this.avatarPlaceholder.set({left: thumbLeft, top: rowTop});
        this.bylineText.set({
            left: thumbLeft + AVATAR_SIZE + 8,
            top: rowTop + (AVATAR_SIZE - this.bylineText.height) / 2
        });

        if (this.thumbImage) {
            ProjectCard.coverImage(
                this.thumbImage,
                thumbWidth,
                thumbHeight,
                thumbLeft,
                thumbTop,
                false
            );
        }
        if (this.avatarImage) {
            ProjectCard.coverImage(
                this.avatarImage,
                AVATAR_SIZE,
                AVATAR_SIZE,
                thumbLeft,
                rowTop,
                true
            );
        }

        this.refreshPlaceholderGlyphs(
            thumbLeft + thumbWidth / 2,
            thumbTop + thumbHeight / 2,
            thumbLeft + AVATAR_SIZE / 2,
            rowTop + AVATAR_SIZE / 2
        );

        this.thumbPlaceholder.visible = !this.thumbImage;
        this.avatarPlaceholder.visible = !this.avatarImage;

        this.triggerLayout();
        this.set({left, top});
        this.setCoords();
        this.dirty = true;
    }

    private refreshPlaceholderGlyphs(
        thumbCenterX: number,
        thumbCenterY: number,
        avatarCenterX: number,
        avatarCenterY: number
    ) {
        this.glyphs.forEach(glyph => this.remove(glyph));
        this.glyphs = [];

        if (!this.thumbImage) {
            this.glyphs.push(
                makeGlyph(
                    customIconNodes.chalkboard,
                    28,
                    PLACEHOLDER_GLYPH_COLOR,
                    thumbCenterX,
                    thumbCenterY
                )
            );
        }
        if (!this.avatarImage) {
            this.glyphs.push(
                makeGlyph(
                    User,
                    12,
                    PLACEHOLDER_GLYPH_COLOR,
                    avatarCenterX,
                    avatarCenterY
                )
            );
        }

        this.glyphs.forEach(glyph => this.add(glyph));
    }

    /*
     * Loads the thumbnail and avatar and swaps them in. Each is independent and
     * each failure is survivable: a card with a dead thumbnail still shows its
     * title, byline and avatar. Nothing here fires a canvas event, so filling a
     * restored card in does not write back to the board.
     */
    async loadImages(): Promise<void> {
        const [thumb, avatar] = await Promise.all([
            ProjectCard.tryLoad(this.cardThumbnail),
            ProjectCard.tryLoad(this.cardUserThumbnail)
        ]);

        if (!thumb && !avatar) {
            return;
        }

        /*
         * Group.add runs the group's layout, and an image arrives at its full
         * natural size at the origin, so adding one before it has been placed
         * drags the whole card off its position. Anchor first, add, then let
         * layoutContent reposition from the anchor.
         */
        const anchor = {left: this.left, top: this.top};

        if (thumb) {
            this.thumbImage = thumb;
            this.add(thumb);
        }
        if (avatar) {
            this.avatarImage = avatar;
            this.add(avatar);
        }

        this.set(anchor);
        this.layoutContent();
        this.canvas?.requestRenderAll();
    }

    private static async tryLoad(url: string): Promise<FabricImage | null> {
        if (!url) {
            return null;
        }

        try {
            return await FabricImage.fromURL(url, {crossOrigin: 'anonymous'});
        } catch (error) {
            console.warn('Project card image failed to load: ' + url, error);
            return null;
        }
    }

    get title(): string {
        return this.cardName;
    }

    get mixId(): string {
        return this.cardMixId;
    }

    get color(): string {
        return this.body.fill as string;
    }

    get target(): ProjectCardTarget {
        return {
            tab: this.cardTab ?? null,
            tabTitle: this.cardTabTitle,
            fullscreen: this.cardFullscreen
        };
    }

    setTarget({tab, tabTitle, fullscreen}: ProjectCardTarget) {
        this.cardTab = tab;
        this.cardTabTitle = tab ? tabTitle : null;
        this.cardFullscreen = Boolean(tab) && fullscreen;
        this.refreshNav();
        this.layoutContent();
    }

    setColor(color: string) {
        this.body.set('fill', color);
        this.dirty = true;
    }

    /*
     * Applies persisted properties for initial restore diffs and remote merges.
     */
    applyElementProps(properties: Record<string, unknown>) {
        const {
            projectRef,
            mixId,
            name,
            userName,
            nickname,
            thumbnail,
            userThumbnail,
            fill,
            width,
            height,
            tab,
            tabTitle,
            fullscreen,
            ...groupProperties
        } = properties;

        void height; // derived from the content
        void userName; // no longer stored or shown; present in older saved cards

        let imagesChanged = false;

        if (typeof projectRef === 'string') {
            this.projectRef = projectRef;
        }
        if (typeof tab === 'string' || tab === null) {
            this.cardTab = tab;
        }
        if (typeof tabTitle === 'string' || tabTitle === null) {
            this.cardTabTitle = tabTitle;
        }
        if (typeof fullscreen === 'boolean') {
            this.cardFullscreen = fullscreen;
        }
        this.refreshNav();
        if (typeof mixId === 'string') {
            this.cardMixId = mixId;
        }
        if (typeof name === 'string') {
            this.cardName = name;
            this.titleText.set('text', name || 'Project');
        }
        if (typeof nickname === 'string') {
            this.cardNickname = nickname;
        }
        if (typeof thumbnail === 'string') {
            const next = ProjectCard.storableUrl(thumbnail);
            if (next !== this.cardThumbnail) {
                this.cardThumbnail = next;
                if (this.thumbImage) {
                    this.remove(this.thumbImage);
                    this.thumbImage = null;
                }
                imagesChanged = true;
            }
        }
        if (typeof userThumbnail === 'string') {
            const next = ProjectCard.storableUrl(userThumbnail);
            if (next !== this.cardUserThumbnail) {
                this.cardUserThumbnail = next;
                if (this.avatarImage) {
                    this.remove(this.avatarImage);
                    this.avatarImage = null;
                }
                imagesChanged = true;
            }
        }
        if (typeof fill === 'string') {
            this.setColor(fill);
        }
        if (typeof width === 'number') {
            this.baseWidth = Math.max(MIN_WIDTH, width);
        }

        this.set(groupProperties);
        this.layoutContent();
        this.dirty = true;
        this.setCoords();

        if (imagesChanged) {
            void this.loadImages();
        }
    }

    /*
     * Serialized as a compact option bag. There must never be an objects array
     * in here: that is what made the old card cost 11.5KB.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    toObject(): any {
        return {
            type: 'project-card',
            left: this.left,
            top: this.top,
            width: this.baseWidth,
            scaleX: this.scaleX,
            scaleY: this.scaleY,
            angle: this.angle,
            flipX: this.flipX,
            flipY: this.flipY,
            projectRef: this.projectRef,
            mixId: this.cardMixId,
            name: this.cardName,
            nickname: this.cardNickname,
            thumbnail: this.cardThumbnail,
            userThumbnail: this.cardUserThumbnail,
            fill: this.color,
            // Left out until a tab has been picked, so older boards and cards
            // that open the first tab serialize exactly as before.
            ...(this.cardTab !== undefined && {
                tab: this.cardTab,
                tabTitle: this.cardTabTitle,
                fullscreen: this.cardFullscreen
            })
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static async fromObject(object: any): Promise<ProjectCard> {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {type, version, ...options} = object ?? {};
        const card = new ProjectCard(options as ProjectCardOptions);
        // Awaited so a duplicated card comes back complete rather than blank.
        await card.loadImages();
        return card;
    }
}

classRegistry.setClass(ProjectCard);
